import { useMemo, useCallback } from "react";
import { useLocation, useHistory } from "react-router-dom";

/**
 * Custom hook to manage table query parameters such as `page` and `page_size` in the URL.
 * It provides the current page, page size, and a method to update these query parameters.
 *
 * @param defaultPageSize - The default number of items per page if `page_size` is not specified in the URL. Defaults to 10.
 * @returns An object containing:
 * - `page`: The current page number derived from the URL query parameters. Defaults to 1 if not specified.
 * - `pageSize`: The current page size derived from the URL query parameters. Defaults to `defaultPageSize` if not specified.
 * - `setQuery`: A function to update the `page` and/or `page_size` query parameters in the URL.
 *
 * @example
 * const { page, pageSize, setQuery } = usePaginationQuery(20);
 * console.log(page); // Current page number
 * console.log(pageSize); // Current page size
 * setQuery({ page: 2, page_size: 50 }); // Updates the URL with new query parameters
 */
export function usePaginationQuery(defaultPageSize = 10) {
	const { search, pathname, hash } = useLocation();
	const history = useHistory(); // v5 hook

	const params = useMemo(() => new URLSearchParams(search), [search]);
	const page = Number(params.get("page") ?? 1);
	const pageSize = Number(params.get("page_size") ?? defaultPageSize);

	const setQuery = useCallback(
		(next: Partial<{ page: number; page_size: number }>) => {
			const newParams = new URLSearchParams(params);
			if (next.page !== undefined) newParams.set("page", String(next.page));
			if (next.page_size !== undefined)
				newParams.set("page_size", String(next.page_size));

			history.replace({ pathname, search: `?${newParams.toString()}`, hash });
		},
		[params, pathname, hash, history],
	);

	return { page, pageSize, setQuery };
}
